import React, { useEffect } from 'react'

// Third party
import { Tooltip } from '@mui/material'
import clsx from 'clsx'
import { v4 } from 'uuid'

// Redux
import { useAppSelector } from '../../../../store/hooks'
import { selectRegistrationR } from '../../../../store/exhibitor/exhibitorSlice'

import { formatDateWithTime } from '../../../../helpers/time'

import TooltipIcon from '../../../../helpers/TooltipIcon'

import { IRegistrationFeesInterface } from '../../../../models/registration-fees/registrationFees.interface'

import { IPaymentStatus, PAYMENT_STATUS } from '../../../../types/payment'
import { getFloatPrice } from '../../../../helpers/price'

// Types
type IExhibitorFeesAccordionProps = {
  data: IRegistrationFeesInterface[]
  showScratchedView: boolean
  setListToScratch: React.Dispatch<React.SetStateAction<string[]>>
  listToScratch: string[]
  currency: string
}

type IDetailRowProps = {
  title: string | null
  date: string
  cost?: number
  units: number
  itemId?: string | null
  isScratched: boolean
  showScratchedView: boolean
  paymentStatus: IPaymentStatus
  listToScratch: string[]
  handleScratchList: (itemId: string) => void
  member: IRegistrationFeesInterface
  currency: string
}

const ExhibitorFeesInput: React.FC<{
  className?: string
  data?: any
  icon?: any
}> = ({ className, data, icon }) => {
  return (
    <>
      {icon ? (
        <div className={className}>
          <img className="absolute left-3 top-3" src={icon} alt="icons" />
          {data}
        </div>
      ) : (
        <div className={className}>{data}</div>
      )}
    </>
  )
}

const DetailRow = ({
  title,
  cost,
  units,
  showScratchedView,
  isScratched,
  listToScratch,
  member,
  date,
  paymentStatus,
  itemId,
  handleScratchList,
  currency,
}: IDetailRowProps) => {
  let id = `${v4()}${Date.now()}`
  let checked = member.id ? listToScratch.find((data) => data === member.id) : false

  return (
    <div className="border rounded-md shadow-sm border-[#D3DAEE] lg:border-0 lg:shadow-none p-2 lg:p-0 mt-4 lg:mt-0 flex flex-col lg:flex-row lg:items-center w-full">
      <div className="lg:hidden font-medium text-[12px]">Line item</div>
      <ExhibitorFeesInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4"
        data={title}
      />

      <div className="lg:hidden mt-2 font-medium text-[12px]">Date</div>
      <ExhibitorFeesInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4"
        data={date}
      />

      <div className="lg:hidden mt-2 font-medium text-[12px]">Number of units</div>

      <ExhibitorFeesInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center"
        data={units}
      />
      <div className="lg:hidden mt-2 font-medium text-[12px]">Price per unit</div>
      <ExhibitorFeesInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center"
        data={getFloatPrice(`${currency}${cost}`)}
      />
      <div className="lg:hidden mt-2 font-medium text-[12px]">Total Price</div>
      <ExhibitorFeesInput
        className="relative rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center qwqwqwqwqwqw"
        data={
          isScratched ? 'Scratched' : `${getFloatPrice(`${currency}${Number(cost || 0) * units}`)}`
        }
        icon={
          paymentStatus === PAYMENT_STATUS.PAID
            ? '/assets/img/dark/credit-card.svg'
            : '/assets/img/dark/cash.svg'
        }
      />

      {showScratchedView && !!itemId ? (
        <label
          htmlFor={id}
          className={clsx(
            'rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/4 text-center relative flex items-center justify-center',
            !isScratched && 'cursor-pointer'
          )}
        >
          {isScratched ? (
            'Scratched'
          ) : (
            <>
              Scratch
              <div
                className={clsx(
                  'ml-2 !ring-0 !shadow-none absolute shrink-0 flex items-center justify-center',
                  !checked
                    ? 'border-2 border-SeabiscuitDark200ThemeColor rounded-full w-5 h-5 right-[14px]'
                    : 'w-6 h-6 right-3'
                )}
              >
                {checked ? (
                  <img
                    src="/assets/og_icons/Ok-3.svg"
                    alt="Ok"
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
              <input
                type="checkbox"
                className="hidden"
                id={id}
                checked={!!checked}
                onChange={() => handleScratchList(itemId as string)}
              />
            </>
          )}
        </label>
      ) : null}
    </div>
  )
}

const ExhibitorFeesAccordion = (props: IExhibitorFeesAccordionProps) => {
  // Hooks and vars
  const { showScratchedView, setListToScratch, listToScratch, currency } = props

  const registrationR = useAppSelector(selectRegistrationR)

  const data = props.data

  useEffect(() => {
    setListToScratch([])
  }, [showScratchedView, registrationR])

  // Functions
  const handleScratchList = (itemId: string) => {
    if (listToScratch.find((data) => data === itemId)) {
      let filter = listToScratch.filter((data) => data !== itemId)
      setListToScratch(filter)
    } else {
      setListToScratch([...listToScratch, itemId])
    }
  }

  return (
    <>
      <div className="hidden lg:flex items-center w-full">
        <ExhibitorFeesInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Line item</span>}
        />

        <ExhibitorFeesInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Date</span>}
        />

        <ExhibitorFeesInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Number of units</span>}
        />

        <ExhibitorFeesInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Price per unit</span>}
        />

        <ExhibitorFeesInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Total Price</span>}
        />

        {showScratchedView ? (
          <ExhibitorFeesInput
            className={clsx(
              'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/5'
            )}
            data={
              <span className="flex items-center">
                Scratch
                <Tooltip title={<h1 className="tooltip_title">Scratch</h1>} placement="top" arrow>
                  <button className="">
                    <TooltipIcon color="#122B46" />
                  </button>
                </Tooltip>
              </span>
            }
          />
        ) : null}
      </div>

      {data && data.length
        ? data.map((currentItem, index) => {
            return (
              <DetailRow
                key={index}
                title={currentItem.feesTitle}
                units={currentItem.selectedUnitsCount}
                itemId={currentItem?.id}
                date={currentItem.modified ? formatDateWithTime(currentItem.modified) : ''}
                listToScratch={listToScratch}
                cost={currentItem.feesPrice as any}
                member={currentItem}
                showScratchedView={showScratchedView}
                handleScratchList={handleScratchList}
                isScratched={currentItem?.isScratched ?? false}
                paymentStatus={currentItem?.paymentStatus ?? null}
                currency={currency}
              />
            )
          })
        : null}
    </>
  )
}

export default ExhibitorFeesAccordion
